import React, { useState, useEffect, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { expensesAPI, shiftsAPI, medicsAPI } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { fmt, symbolFor } from '../../lib/currency';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Progress } from '../../components/ui/progress';
import { 
  Plus,
  Receipt,
  Clock,
  CheckCircle,
  XCircle,
  Upload,
  FileImage,
  File,
  X,
  AlertCircle,
  Calendar
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

const STATUS_STYLES = {
  pending: 'bg-[#FFC04D]/15 text-[#FFC04D] border-[#FFC04D]/20',
  approved: 'bg-[#1ABC9C]/15 text-[#1ABC9C] border-[#1ABC9C]/20',
  rejected: 'bg-red-500/15 text-red-400 border-red-500/20',
};

const EXPENSE_CATEGORIES = [
  'Travel',
  'Meals',
  'Equipment',
  'Parking',
  'Accommodation',
  'Other',
];

export default function MedicExpenses() {
  const { user } = useAuth();
  const orgCurrency = user?.organisation_currency || 'GBP';
  const [expenses, setExpenses] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [medicProfile, setMedicProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [receiptFile, setReceiptFile] = useState(null);
  
  const [newExpense, setNewExpense] = useState({
    shift_id: '',
    amount: '',
    category: '',
    expense_date: '',
    description: '',
  });

  const onDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0];
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File too large. Maximum size is 5MB');
        return;
      }
      setReceiptFile(file);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
      'application/pdf': ['.pdf']
    },
    maxFiles: 1
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [expensesRes, profileRes, shiftsRes] = await Promise.all([
        expensesAPI.list(),
        medicsAPI.getMyProfile(),
        shiftsAPI.list(),
      ]);
      
      setExpenses(expensesRes.data);
      setMedicProfile(profileRes.data);
      
      // Filter shifts for this medic
      const myShifts = shiftsRes.data.filter(s => s.medic_id === profileRes.data.id);
      setShifts(myShifts);
    } catch (error) {
      toast.error('Failed to load expenses');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateExpense = async () => {
    if (!newExpense.shift_id || !newExpense.amount || !newExpense.category) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (!receiptFile) {
      toast.error('Please upload a receipt');
      return;
    }

    try {
      // Step 1: Create expense
      const response = await expensesAPI.create({
        shift_id: newExpense.shift_id,
        amount: parseFloat(newExpense.amount),
        category: newExpense.category,
        expense_date: newExpense.expense_date || null,
        description: newExpense.description || null,
      });

      // Step 2: Upload receipt
      setUploading(true);
      setUploadProgress(30);
      
      await expensesAPI.uploadReceipt(response.data.id, receiptFile);
      setUploadProgress(100);
      
      toast.success('Expense submitted successfully');
      setShowCreateModal(false);
      setNewExpense({
        shift_id: '',
        amount: '',
        category: '',
        expense_date: '',
        description: '',
      });
      setReceiptFile(null);
      setUploadProgress(0);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit expense');
    } finally {
      setUploading(false);
    }
  };

  // Calculate totals
  const totals = {
    pending: expenses.filter(e => e.status === 'pending').reduce((acc, e) => acc + e.amount, 0),
    approved: expenses.filter(e => e.status === 'approved').reduce((acc, e) => acc + e.amount, 0),
    rejected: expenses.filter(e => e.status === 'rejected').reduce((acc, e) => acc + e.amount, 0),
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-10 h-10 border-4 border-[#1ABC9C]/20 border-t-[#1ABC9C] rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="medic-expenses-container">
      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="bg-[#2B2B2B] border-white/5">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-[#FFC04D]/15">
                <Clock className="w-5 h-5 text-[#FFC04D]" />
              </div>
              <div>
                <p className="text-sm text-[#A1A1AA]">Pending</p>
                <p className="text-xl font-bold text-[#F2F2F2] font-['JetBrains_Mono']">
                  {fmt(totals.pending, orgCurrency)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-[#2B2B2B] border-white/5">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-[#1ABC9C]/15">
                <CheckCircle className="w-5 h-5 text-[#1ABC9C]" />
              </div>
              <div>
                <p className="text-sm text-[#A1A1AA]">Approved</p>
                <p className="text-xl font-bold text-[#F2F2F2] font-['JetBrains_Mono']">
                  {fmt(totals.approved, orgCurrency)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-[#2B2B2B] border-white/5">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-red-500/15">
                <XCircle className="w-5 h-5 text-red-400" />
              </div>
              <div>
                <p className="text-sm text-[#A1A1AA]">Rejected</p>
                <p className="text-xl font-bold text-[#F2F2F2] font-['JetBrains_Mono']">
                  {fmt(totals.rejected, orgCurrency)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-semibold text-[#F2F2F2]">My Expenses</h2>
        <Button
          className="bg-[#1ABC9C] hover:bg-[#1ABC9C]/90"
          onClick={() => setShowCreateModal(true)}
          data-testid="submit-expense-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Submit Expense
        </Button>
      </div>

      {/* Expenses List */}
      {expenses.length === 0 ? (
        <Card className="bg-[#2B2B2B] border-white/5">
          <CardContent className="p-8 text-center">
            <Receipt className="w-12 h-12 mx-auto mb-3 text-[#A1A1AA] opacity-50" />
            <p className="text-[#A1A1AA]">No expenses submitted yet</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {expenses.map(expense => (
            <Card key={expense.id} className="bg-[#2B2B2B] border-white/5">
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="space-y-2 flex-1">
                    <div className="flex items-center gap-3">
                      <span className="font-medium text-[#F2F2F2]">{expense.category}</span>
                      <Badge className={STATUS_STYLES[expense.status]}>
                        {expense.status}
                      </Badge>
                      {expense.receipt_file_name && (
                        <Badge variant="outline" className="text-xs border-white/20">
                          <FileImage className="w-3 h-3 mr-1" />
                          Receipt
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-[#A1A1AA]">
                      {expense.description || 'No description'}
                    </p>
                    <div className="flex items-center gap-4 text-xs text-[#A1A1AA]">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {expense.expense_date 
                          ? format(new Date(expense.expense_date), 'MMM dd, yyyy')
                          : format(new Date(expense.created_at), 'MMM dd, yyyy')
                        }
                      </span>
                      {expense.shift_location && (
                        <span>Shift: {expense.shift_location}</span>
                      )}
                    </div>
                    {expense.status === 'rejected' && expense.reviewer_notes && (
                      <div className="mt-2 p-2 bg-red-500/10 rounded border border-red-500/20">
                        <p className="text-xs text-red-400 flex items-start gap-2">
                          <AlertCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                          <span><strong>Rejection reason:</strong> {expense.reviewer_notes}</span>
                        </p>
                      </div>
                    )}
                    {expense.status === 'approved' && expense.reviewer_name && (
                      <p className="text-xs text-[#1ABC9C]">
                        Approved by {expense.reviewer_name} on {format(new Date(expense.reviewed_at), 'MMM dd')}
                      </p>
                    )}
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-[#1ABC9C] font-['JetBrains_Mono']">
                      {fmt(expense.amount, expense.currency)}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create Expense Modal */}
      <Dialog open={showCreateModal} onOpenChange={(open) => {
        setShowCreateModal(open);
        if (!open) {
          setReceiptFile(null);
          setUploadProgress(0);
        }
      }}>
        <DialogContent className="bg-[#2B2B2B] border-white/10 text-[#F2F2F2] max-w-md">
          <DialogHeader>
            <DialogTitle className="font-['Manrope']">Submit Expense</DialogTitle>
            <DialogDescription className="text-[#A1A1AA]">
              Submit an expense claim with receipt for reimbursement
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Select Shift *</Label>
              <Select 
                value={newExpense.shift_id} 
                onValueChange={(value) => setNewExpense(prev => ({ ...prev, shift_id: value }))}
              >
                <SelectTrigger className="bg-[#1A1A1A] border-white/10">
                  <SelectValue placeholder="Select a shift" />
                </SelectTrigger>
                <SelectContent className="bg-[#2B2B2B] border-white/10">
                  {shifts.length === 0 ? (
                    <SelectItem value="none" disabled>No shifts available</SelectItem>
                  ) : (
                    shifts.map(shift => (
                      <SelectItem key={shift.id} value={shift.id}>
                        {format(new Date(shift.date), 'MMM dd')} - {shift.location}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Category *</Label>
              <Select 
                value={newExpense.category} 
                onValueChange={(value) => setNewExpense(prev => ({ ...prev, category: value }))}
              >
                <SelectTrigger className="bg-[#1A1A1A] border-white/10">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent className="bg-[#2B2B2B] border-white/10">
                  {EXPENSE_CATEGORIES.map(cat => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Amount ({symbolFor(orgCurrency)}) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={newExpense.amount}
                  onChange={(e) => setNewExpense(prev => ({ ...prev, amount: e.target.value }))}
                  placeholder="0.00"
                  className="bg-[#1A1A1A] border-white/10"
                />
              </div>
              <div className="space-y-2">
                <Label>Expense Date</Label>
                <Input
                  type="date"
                  value={newExpense.expense_date}
                  onChange={(e) => setNewExpense(prev => ({ ...prev, expense_date: e.target.value }))}
                  className="bg-[#1A1A1A] border-white/10"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={newExpense.description}
                onChange={(e) => setNewExpense(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Brief description of expense"
                className="bg-[#1A1A1A] border-white/10"
                rows={2}
              />
            </div>

            <div className="space-y-2">
              <Label>Receipt *</Label>
              {!receiptFile ? (
                <div
                  {...getRootProps()}
                  className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
                    isDragActive 
                      ? 'border-[#1ABC9C] bg-[#1ABC9C]/10' 
                      : 'border-white/20 hover:border-white/40'
                  }`}
                >
                  <input {...getInputProps()} />
                  <Upload className="w-8 h-8 mx-auto mb-2 text-[#A1A1AA]" />
                  <p className="text-sm text-[#A1A1AA]">
                    {isDragActive ? 'Drop the file here' : 'Drag & drop or click to upload'}
                  </p>
                  <p className="text-xs text-[#A1A1AA] mt-1">JPG, PNG or PDF (max 5MB)</p>
                </div>
              ) : (
                <div className="flex items-center justify-between p-3 bg-[#1A1A1A] rounded-lg border border-white/10">
                  <div className="flex items-center gap-3">
                    {receiptFile.type === 'application/pdf' ? (
                      <File className="w-8 h-8 text-red-400" />
                    ) : (
                      <FileImage className="w-8 h-8 text-[#1ABC9C]" />
                    )}
                    <div>
                      <p className="text-sm text-[#F2F2F2] truncate max-w-[180px]">{receiptFile.name}</p>
                      <p className="text-xs text-[#A1A1AA]">
                        {(receiptFile.size / 1024).toFixed(1)} KB
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setReceiptFile(null)}
                    className="text-[#A1A1AA] hover:text-red-400"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </div>

            {uploading && (
              <div className="space-y-2">
                <div className="flex justify-between text-xs text-[#A1A1AA]">
                  <span>Uploading receipt...</span>
                  <span>{uploadProgress}%</span>
                </div>
                <Progress value={uploadProgress} className="h-2" />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowCreateModal(false)} className="text-[#A1A1AA]">
              Cancel
            </Button>
            <Button 
              onClick={handleCreateExpense} 
              className="bg-[#1ABC9C] hover:bg-[#1ABC9C]/90"
              disabled={uploading}
            >
              {uploading ? 'Submitting...' : 'Submit Expense'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
